import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Copy } from 'lucide-react';
import Button from '../../components/Button';
import { WEEKDAYS } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import { getStoreHours, updateStoreHours } from '../../api/store';
import styles from './StoreHoursCard.module.css';

function formatTime(value) {
  const [h, m] = value.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function toMinutes(value) {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Closing time must be later than opening time on the same day. It's easy to
// pick the wrong AM/PM on a time field without noticing, so flag it rather
// than silently accepting a schedule that doesn't make sense.
function isDayInvalid(day) {
  return day.open && toMinutes(day.closesAt) <= toMinutes(day.opensAt);
}

export default function StoreHoursCard() {
  const { user } = useAuth();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [openNow, setOpenNow] = useState(false);
  const [emergency, setEmergency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [copyFrom, setCopyFrom] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const popoverRef = useRef(null);

  const dirty = useMemo(() => !!saved && !!draft && JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);
  const hasInvalidDay = useMemo(() => !!draft && WEEKDAYS.some((day) => isDayInvalid(draft[day])), [draft]);

  const loadHours = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const store = await getStoreHours(user.idToken);
      setSaved(store.weekly);
      setDraft(store.weekly);
      setOpenNow(store.isOpenNow);
      setEmergency({ closed: store.emergencyClosed, reason: store.emergencyReason });
    } catch (err) {
      setLoadError(err.message || 'Could not load store hours.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    loadHours();
  }, [loadHours]);

  useEffect(() => {
    if (!copyFrom) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setCopyFrom(null);
        setCopyTargets([]);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [copyFrom]);

  const updateDay = (day, patch) => {
    setDraft((d) => ({ ...d, [day]: { ...d[day], ...patch } }));
  };

  const openCopyFor = (day) => {
    setCopyFrom(copyFrom === day ? null : day);
    setCopyTargets([]);
  };

  const toggleCopyTarget = (day) => {
    setCopyTargets((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const applyCopy = () => {
    const source = draft[copyFrom];
    setDraft((d) => {
      const next = { ...d };
      copyTargets.forEach((day) => { next[day] = { ...source }; });
      return next;
    });
    setCopyFrom(null);
    setCopyTargets([]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (hasInvalidDay || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const store = await updateStoreHours(draft, user.idToken);
      setSaved(store.weekly);
      setDraft(store.weekly);
      setOpenNow(store.isOpenNow);
      setEmergency({ closed: store.emergencyClosed, reason: store.emergencyReason });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    } catch (err) {
      setSaveError(err.message || 'Could not save store hours.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.iconWrap}><Clock size={18} /></span>
        <div className={styles.headText}>
          <p className={styles.title}>Store hours</p>
          <p className={styles.subtitle}>Set opening and closing times per day — customers can only order while you're open.</p>
        </div>
        {!loading && !loadError && (
          <span className={`${styles.statusPill} ${openNow ? styles.open : styles.closed}`}>
            <span className={styles.statusDot} />
            {openNow ? 'Open now' : 'Closed now'}
          </span>
        )}
      </div>

      <form onSubmit={handleSave} className={styles.body}>
        {loading && <p className={styles.hint}>Loading store hours…</p>}

        {!loading && loadError && (
          <div className={styles.hint}>
            <p className={styles.invalidWarning} style={{ margin: 0 }}>
              <AlertTriangle size={12} /> {loadError}
            </p>
            <Button type="button" variant="outline" size="sm" onClick={loadHours} style={{ marginTop: 8 }}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !loadError && draft && (
          <>
            {emergency?.closed && (
              <p className={styles.invalidWarning} style={{ margin: '0 0 12px' }}>
                <AlertTriangle size={12} />
                Emergency closed{emergency.reason ? `: ${emergency.reason}` : ''} — manage this from the store status control in the top bar.
              </p>
            )}

            <p className={styles.hint}>
              Double-check AM / PM — closing time must be later than opening time on the same day.
            </p>
            <div className={styles.dayList}>
              {WEEKDAYS.map((day) => {
                const d = draft[day];
                const invalid = isDayInvalid(d);
                return (
                  <div className={styles.dayRow} key={day}>
                    <div className={styles.dayRowMain}>
                      <button
                        type="button"
                        className={`${styles.dayToggle} ${d.open ? styles.dayToggleOn : ''}`}
                        onClick={() => updateDay(day, { open: !d.open })}
                        aria-label={`Toggle ${day}`}
                      />
                      <span className={styles.dayLabel}>{day}</span>

                      {d.open ? (
                        <div className={styles.timeFields}>
                          <input
                            type="time"
                            className={`${styles.timeInput} ${invalid ? styles.timeInputInvalid : ''}`}
                            value={d.opensAt}
                            onChange={(e) => updateDay(day, { opensAt: e.target.value })}
                          />
                          <span className={styles.timeSep}>–</span>
                          <input
                            type="time"
                            className={`${styles.timeInput} ${invalid ? styles.timeInputInvalid : ''}`}
                            value={d.closesAt}
                            onChange={(e) => updateDay(day, { closesAt: e.target.value })}
                          />
                          {!invalid && (
                            <span className={styles.duration}>
                              {formatDuration(toMinutes(d.closesAt) - toMinutes(d.opensAt))}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className={styles.closedLabel}>Closed all day</span>
                      )}

                      <div className={styles.copyAnchor}>
                        <button type="button" className={styles.copyBtn} onClick={() => openCopyFor(day)} aria-label={`Copy ${day}'s hours to other days`}>
                          <Copy size={13} />
                        </button>
                        {copyFrom === day && (
                          <div className={styles.popover} ref={popoverRef}>
                            <p className={styles.popoverTitle}>Copy {day}'s hours to</p>
                            <div className={styles.popoverDays}>
                              {WEEKDAYS.filter((x) => x !== day).map((target) => (
                                <label key={target} className={styles.popoverCheck}>
                                  <input
                                    type="checkbox"
                                    checked={copyTargets.includes(target)}
                                    onChange={() => toggleCopyTarget(target)}
                                  />
                                  {target}
                                </label>
                              ))}
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              disabled={copyTargets.length === 0 || invalid}
                              onClick={applyCopy}
                              className={styles.popoverApply}
                            >
                              Apply to {copyTargets.length || ''} day{copyTargets.length === 1 ? '' : 's'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {invalid && (
                      <p className={styles.invalidWarning}>
                        <AlertTriangle size={12} /> The timing entered is invalid
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <p className={styles.summary}>
              {WEEKDAYS.filter((d) => draft[d].open).length === 0
                ? 'Store is closed every day right now.'
                : WEEKDAYS.map((d) => (draft[d].open ? `${d} ${formatTime(draft[d].opensAt)}–${formatTime(draft[d].closesAt)}` : `${d} closed`)).join(' · ')}
            </p>

            {saveError && (
              <p className={styles.invalidWarning} style={{ margin: '0 0 12px' }}>
                <AlertTriangle size={12} /> {saveError}
              </p>
            )}

            <Button type="submit" size="sm" disabled={!dirty || hasInvalidDay || saving} className={styles.saveBtn}>
              {saving ? 'Saving…' : justSaved ? <><CheckCircle2 size={14} /> Saved</> : 'Save store hours'}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
